import { useMemo } from "react";
import { Link } from "react-router-dom";
import { menuPath } from "@/domain/menus";
import { Icon } from "@/components/ui/Icon";
import { useRqcShipments } from "@/hooks/useRqcShipments";
import { useLastShipment } from "@/hooks/useLastShipment";
import { kstToday } from "@/domain/rqc";
import { TodayStatusCardV2View } from "@/pages/Dispatch/TodayStatusCardV2View";
import { useAsconShipments } from "@/hooks/useAsconShipments";
import { asconOrdersToCardRows } from "@/domain/asconShipment";
import { shortCarNo } from "@/pages/Dispatch/ascon/carNo";
import { TenantBadge } from "./ScheduleCards";

/**
 * 내 업무 홈 ① 오늘 — 법인별 "금일 출하/생산 현황" 카드 (B′ 미리보기 2차, 2026-10-19).
 *
 * 대시보드 금일 출하 현황 카드(Dashboard.tsx TodayStatusCardV2)와 **같은 표시 컴포넌트**
 * (pages/Dispatch/TodayStatusCardV2View)를 inline 형으로 쓴다 — 예정·출하·잔량·진행률 + 진행률 막대.
 * inline 형은 다른 카드 안에 끼우는 형(홈 MO 통합 카드)이라 진행률 막대가 흐름 안에 있다(widget-tile 불필요).
 *
 * - 중앙: useRqcShipments(kstToday()) + useLastShipment — 대시보드와 같은 소스(실데이터, 1분 폴링).
 *         이 컴포넌트는 출하 권한자에게만 마운트한다(마운트 = 조회 시작).
 * - 두 카드 모두 제목 "출하 현황" + 제목 옆 법인 칩(2026-10-22).
 * - 경원: OKIS 미러 실시간 구독(useAsconShipments — 경원 출하조회와 같은 소스, 2026-10-22), 단위 톤, 계획 칸 이름 "수주".
 *         수집기가 멈추면 부제에 안내. 가상 자료(?ascon=mock)일 때만 "가상" 표시. 출하 권한자에게만 마운트한다.
 */

const dateLabel = () =>
  new Date().toLocaleDateString("ko-KR", { month: "long", day: "numeric", weekday: "short" });

export function JoongangTodayCard() {
  const today = kstToday();
  const { shipments, error } = useRqcShipments(today);
  const lastShipment = useLastShipment(today);
  return (
    <section className="card my-prod-card">
      <TodayStatusCardV2View
        layout="inline"
        title="출하 현황"
        titleAddon={<TenantBadge t="joongang" />}
        shipments={shipments}
        sub={error ? "불러오지 못했습니다 · 잠시 뒤 다시 시도합니다" : `${dateLabel()} · 레미콘`}
        lastShipment={lastShipment}
        actions={
          <div className="my-prod-actions">
            <Link to={menuPath("dispatch-view", "joongang")} className="my-more-link" aria-label="출하조회로 이동">
              출하조회 <Icon name="chevR" size={12} />
            </Link>
          </div>
        }
      />
    </section>
  );
}

export function GyeongwonTodayCard() {
  const today = kstToday();
  const { orders, tickets, source, error } = useAsconShipments(today);
  const shipments = useMemo(() => asconOrdersToCardRows(orders, tickets), [orders, tickets]);
  const lastShipment = useMemo(() => {
    const last = tickets.reduce<(typeof tickets)[number] | null>((a, t) => (!a || t.time > a.time ? t : a), null);
    return last ? { time: last.time, carNo: shortCarNo(last.carNo) } : null;
  }, [tickets]);
  return (
    <section className="card my-prod-card">
      <TodayStatusCardV2View
        layout="inline"
        title="출하 현황"
        titleAddon={<TenantBadge t="gyeongwon" />}
        unit="톤"
        planLabel="수주"
        shipments={shipments}
        sub={error ?? `${dateLabel()} · 아스콘`}
        lastShipment={lastShipment}
        actions={
          <div className="my-prod-actions">
            {source === "mock" && <span className="my-tag is-mock">가상</span>}
            <Link to={menuPath("dispatch-view", "gyeongwon")} className="my-more-link" aria-label="경원 출하조회로 이동">
              출하조회 <Icon name="chevR" size={12} />
            </Link>
          </div>
        }
      />
    </section>
  );
}
